import React, { useEffect, useRef } from 'react'
import { navigate } from 'gatsby'
import { motion } from 'framer-motion'
import useReducedMotionPreference from '../hooks/useReducedMotionPreference'
import styled from 'styled-components'
import { usePageTransition } from '../context/PageTransitionContext'
import NtLogo58px from '../images/nt-logo-58px.svg'

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgb(var(--bg-color));
  z-index: 9998;
`

const FloatingTitle = styled(motion.h1)`
  position: fixed;
  z-index: 9999;
  margin: 0;
  padding: 0;
  font-family: 'Merriweather', serif;
  font-weight: 300;
  line-height: 2.1rem;
  letter-spacing: -0.0055rem;
  color: rgb(var(--text-color));
`

const FloatingLogo = styled(motion.div)`
  position: fixed;
  z-index: 9999;

  svg {
    display: block;
    width: 100%;
    height: auto;

    .circle {
      stroke: rgb(var(--text-color));
    }

    .letters {
      fill: rgb(var(--text-color));
    }
  }
`

export default function PageTransitionOverlay() {
  const { transition, setPhase, setTargetRect, endTransition } =
    usePageTransition()
  const hasNavigated = useRef(false)
  const prefersReducedMotion = useReducedMotionPreference()

  const isLogoTransition = transition?.type === 'logo'

  // Navigate when entering 'move' phase
  useEffect(() => {
    if (transition?.phase !== 'move' || hasNavigated.current) return
    hasNavigated.current = true

    navigate(transition.slug)

    const isLogo = transition.type === 'logo'
    const selector = isLogo
      ? '[data-transition-logo]'
      : '[data-transition-title]'

    // Wait for the destination page to render, then scroll and measure
    const timer = setTimeout(async () => {
      window.scrollTo(0, 0)

      // Wait for fonts so typography-based measurements are accurate.
      // Without this, a font swap (e.g. Merriweather loading) can shift the
      // title position after we've already captured targetRect, causing the
      // floating element to land at the wrong spot and the real title to jump.
      try {
        await document.fonts.ready
      } catch (_) {}

      // Double rAF: first frame commits the layout after the font-ready
      // promise resolves, second frame reads a stable geometry.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.querySelector(selector)
          if (el) {
            const target = isLogo ? el.querySelector('svg') || el : el
            const rect = target.getBoundingClientRect()
            if (isLogo) {
              setTargetRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
              })
            } else {
              const styles = window.getComputedStyle(el)
              setTargetRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                fontSize: styles.fontSize,
                lineHeight: styles.lineHeight,
                letterSpacing: styles.letterSpacing,
              })
            }
          } else {
            // Target not found, just reveal the page
            setPhase('enter')
          }
        })
      })
    }, 150)

    return () => clearTimeout(timer)
  }, [
    transition?.phase,
    transition?.slug,
    transition?.type,
    setTargetRect,
    setPhase,
  ])

  // Reset navigation flag when transition ends
  useEffect(() => {
    if (!transition) hasNavigated.current = false
  }, [transition])

  // Reduced motion: skip animation, just navigate
  useEffect(() => {
    if (transition && prefersReducedMotion) {
      navigate(transition.slug)
      endTransition()
    }
  }, [transition, prefersReducedMotion, endTransition])

  // Hide the real element while the floating one is visible
  useEffect(() => {
    if (prefersReducedMotion) return

    const titleEl = document.querySelector('[data-transition-title]')
    if (titleEl) {
      if (
        transition &&
        transition.type !== 'logo' &&
        transition.phase !== 'enter'
      ) {
        titleEl.style.opacity = '0'
      } else {
        titleEl.style.opacity = ''
      }
    }

    const logoEl = document.querySelector('[data-transition-logo]')
    if (logoEl) {
      if (
        transition &&
        transition.type === 'logo' &&
        transition.phase !== 'enter'
      ) {
        logoEl.style.opacity = '0'
      } else {
        logoEl.style.opacity = ''
      }
    }
  }, [transition, transition?.phase, transition?.type, prefersReducedMotion])

  // Ensure real elements are restored on unmount
  useEffect(() => {
    return () => {
      const titleEl = document.querySelector('[data-transition-title]')
      if (titleEl) titleEl.style.opacity = ''
      const logoEl = document.querySelector('[data-transition-logo]')
      if (logoEl) logoEl.style.opacity = ''
    }
  }, [])

  // Safety timeout to prevent stuck transitions
  useEffect(() => {
    if (!transition) return
    const timer = setTimeout(endTransition, 5000)
    return () => clearTimeout(timer)
  }, [transition, endTransition])

  if (!transition || prefersReducedMotion) return null

  const { sourceRect, targetRect, title, phase } = transition

  // Shared animate logic — position + opacity, with font styles for titles only
  const sourceStyles =
    !isLogoTransition && sourceRect
      ? {
          fontSize: sourceRect.fontSize,
          lineHeight: sourceRect.lineHeight,
          letterSpacing: sourceRect.letterSpacing,
        }
      : {}

  const targetStyles =
    !isLogoTransition && targetRect
      ? {
          fontSize: targetRect.fontSize,
          lineHeight: targetRect.lineHeight,
          letterSpacing: targetRect.letterSpacing,
        }
      : {}

  const floatingAnimate = (() => {
    if (phase === 'enter' && targetRect) {
      return {
        top: targetRect.top,
        left: targetRect.left,
        width: targetRect.width,
        ...targetStyles,
        opacity: 0,
      }
    }
    if (phase === 'enter') {
      return { opacity: 0 }
    }
    if (targetRect) {
      return {
        top: targetRect.top,
        left: targetRect.left,
        width: targetRect.width,
        ...targetStyles,
        opacity: 1,
      }
    }
    return { opacity: 1 }
  })()

  const floatingInitial = {
    top: sourceRect.top,
    left: sourceRect.left,
    width: sourceRect.width,
    ...sourceStyles,
    opacity: 1,
  }

  const floatingTransition =
    phase === 'enter'
      ? { duration: 0.3, ease: 'easeOut' }
      : { duration: 0.6, ease: [0.22, 1, 0.36, 1] }

  const handleFloatingComplete = () => {
    if (phase === 'move' && targetRect) {
      setPhase('enter')
    }
  }

  const Floating = isLogoTransition ? FloatingLogo : FloatingTitle

  return (
    <>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === 'enter' ? 0 : 1 }}
        transition={{
          duration: phase === 'enter' ? 0.4 : 0.3,
          ease: 'easeInOut',
        }}
        onAnimationComplete={() => {
          if (phase === 'exit') setPhase('move')
          if (phase === 'enter') endTransition()
        }}
      />
      <Floating
        initial={floatingInitial}
        animate={floatingAnimate}
        transition={floatingTransition}
        onAnimationComplete={handleFloatingComplete}
      >
        {isLogoTransition ? <NtLogo58px /> : title}
      </Floating>
    </>
  )
}
